//@author: devin
import { Proto } from "../pojo/Proto";
import { S25States } from "./state/state.map";
import NumericalString = Proto.NumericalString;
import { S25ItemI } from "../pojo/S25ItemI";
import type { DropDownItem } from "../pojo/DropDownItem";
import { AngBridgeUtil } from "../util/ang.bridge.util";

type SearchViews =
    | "draft_requestor"
    | "draft_scheduler"
    | "event_searches"
    | "event_requestor"
    | "event_scheduler"
    | "event_instructor"
    | "event_all_other"
    | "resource_searches"
    | "space_searches"
    | "task_agenda"
    | "task_active"
    | "task_flagged"
    | "task_byyou"
    | "task_overdue"
    | "task_search";

interface ItemNav {
    /** Optional for navigations that only need type + view (e.g. simple search, favorites calendar). */
    itemId?: number | NumericalString;
    itemTypeId: number | NumericalString;
    forceView?: SearchViews | S25States.ChildNames<"event" | "location" | "resource" | "organization" | "contact">;
    searchTerm?: string;
    itemName?: string;
}

export type RouteHistory = {
    from: { state: { name: S25States.Name }; paramValues: S25ItemI };
    to: { _identifier: { name: S25States.Name } | S25States.Name; _params: S25ItemI };
};

//todo: this is just a proxy class to angularJS StateService; once we migrate states and routing into Angular proper, then
//todo: we need to replace this proxy class with calls to the Angular router and Angular states
export class StateService {
    public static async gotoItem(item: ItemNav, newTab?: boolean, forceReload?: boolean) {
        return (await AngBridgeUtil.$inject("StateService")).gotoItem(item, newTab, forceReload);
    }

    public static async gotoPreviousState(allowSibling?: boolean) {
        return (await AngBridgeUtil.$inject("StateService")).gotoPreviousState(allowSibling);
    }

    public static async getRecentlyViewedItems(): Promise<DropDownItem[]> {
        return (await AngBridgeUtil.$inject("StateService")).getRecentlyViewedItems();
    }

    public static async putRecentlyViewedItem(itemTypeId: number, itemId: number, itemName: string) {
        return (await AngBridgeUtil.$inject("StateService")).putRecentlyViewedItem(itemTypeId, itemId, itemName);
    }

    public static async putRecentlyViewedItemFetchName(itemTypeId: number, itemId: number, itemName?: string) {
        return (await AngBridgeUtil.$inject("StateService")).putRecentlyViewedItemFetchName(
            itemTypeId,
            itemId,
            itemName,
        );
    }

    public static async createEvent(item: any, itemTypeId?: number) {
        return (await AngBridgeUtil.$inject("StateService")).createEvent(item, itemTypeId);
    }

    public static async gotoRose(item?: any) {
        return (await AngBridgeUtil.$inject("StateService")).gotoRose(item);
    }

    public static async go(stateName: string, params: any, options: any, newTab?: any, inheritParams?: boolean) {
        return (await AngBridgeUtil.$inject("StateService")).go(stateName, params, options, newTab, inheritParams);
    }

    /**
     * Build a hash URL for a ui-router state without navigating (wraps $state.href).
     * Prefer this over hand-built #!/home/... strings so URLs follow state.map when routes move.
     * Returns undefined if AngularJS has not yet bootstrapped.
     */
    public static href(stateName: S25States.Name | string, params?: any, options?: any): string | undefined {
        return AngBridgeUtil.$inject.sync("StateService")?.href(stateName, params, options);
    }

    /**
     * Correct the address bar in place, replacing the current history entry rather than navigating.
     * For a URL the router has already arrived at, where go() finds nothing to transition to and so
     * writes no address of its own. Resolves once the address bar has caught up.
     */
    public static async replaceUrl(url: string): Promise<void> {
        return (await AngBridgeUtil.$inject("StateService")).replaceUrl(url);
    }

    public static async closeCurrent() {
        return (await AngBridgeUtil.$inject("StateService")).closeCurrent();
    }

    /**
     * Read the current ui-router query params (hashbang mode -> the hash query). Use this to seed
     * deep-linked state once at construction. Returns {} before AngularJS has bootstrapped.
     */
    public static getSearch(): Record<string, string> {
        const search = AngBridgeUtil.$inject.sync("$location")?.search();
        return search && typeof search === "object" ? search : {};
    }

    /**
     * One-way write of query params into the ui-router URL via $location (router-aware), using replace()
     * so no history entry is added. The owning state must declare these as search params with
     * reloadOnSearch:false so updates are dynamic and do NOT reload/remount the view. Empty values are
     * dropped. No-ops before AngularJS has bootstrapped (e.g. in unit tests).
     */
    public static setSearch(search: Record<string, string | number | undefined>): void {
        const $location = AngBridgeUtil.$inject.sync("$location");
        const $rootScope = AngBridgeUtil.$inject.sync("$rootScope");
        if (!$location || !$rootScope) return;
        const cleaned: Record<string, string> = {};
        for (const key of Object.keys(search)) {
            const value = search[key];
            if (value !== undefined && value !== null && `${value}` !== "") {
                cleaned[key] = `${value}`;
            }
        }
        $location.search(cleaned).replace();
        if (!$rootScope.$$phase) {
            $rootScope.$applyAsync();
        }
    }

    public static current() {
        return AngBridgeUtil.$inject.sync("StateService")?.current();
    }

    public static getState() {
        return AngBridgeUtil.$inject.sync("StateService")?.getState();
    }

    public static getRouteHistory() {
        return AngBridgeUtil.$inject.sync("StateService")?.getRouteHistory();
    }

    public static getRouteToIdentifier(route: RouteHistory | undefined): S25States.Name | undefined {
        const routeIdentifier = route?.to?._identifier;
        if (!routeIdentifier) return undefined;
        return typeof routeIdentifier === "string" ? routeIdentifier : (routeIdentifier.name ?? undefined);
    }

    public static ItemTypeNorm: any = {
        11: 1, //event
        12: 2, //organization
        14: 4, //location
        1104: 4,
        1204: 4,
        407: 4,
        16: 6, //resource
        1106: 6,
        1206: 6,
        607: 6,
        201: 3001, //event group: sugar for simple event search
        204: 3004, //location group: sugar for simple location search
        206: 3006, //resource group: sugar for simple resource search
        10101: 10, //task
        10102: 10,
        10103: 10,
        10104: 10,
        10001: 101, //event search
        10002: 101,
        10003: 101,
        10004: 101,
        10051: 101,
        10052: 101,
    };
}
